import { ProductCode, productEntry, type AuthUser } from '@gb-ticket/shared-types';

import { canOpenAdmin } from '@/platform/lib/admin';
import { START_PATH } from '@/platform/lib/products';

/** Paths that resolve to the personal landing page (not the Dashboard at `/start`). */
const LANDING_ALIASES = new Set(['', '/', '/anmelden']);

/** Intranet is in the session products list (hub tile still links there). */
export function hasIntranetProduct(user: AuthUser): boolean {
  return (user.products ?? []).includes(ProductCode.INTRANET);
}

export function intranetEntryPath(user: AuthUser): string {
  return productEntry(ProductCode.INTRANET, user.permissions).path;
}

/** Logo and „Zurück zur Startseite“ — same rules as defaultHomePath. */
export function appLogoPath(user: AuthUser | null | undefined): string {
  if (user) {
    return defaultHomePath(user);
  }
  return START_PATH;
}

/**
 * Landing page without a deep link.
 * Self-service-only users stay on their product. With Intranet access the landing is Intranet.
 * Otherwise `/start` (Dashboard).
 */
export function defaultHomePath(user: AuthUser): string {
  const entries = (user.products ?? []).map((code) => productEntry(code, user.permissions));
  const [only] = entries;
  if (entries.length === 1 && only?.selfService && !canOpenAdmin(user)) {
    return only.path;
  }
  if (hasIntranetProduct(user)) {
    return intranetEntryPath(user);
  }
  return START_PATH;
}

/**
 * Deep links stay. `/start` is the Dashboard and is not remapped to Intranet.
 * Self-service-only users still leave `/start` for their product entry.
 */
export function resolvePostLoginPath(user: AuthUser, from: string): string {
  const target = from.trim();
  const pathname = target.split(/[?#]/)[0] ?? '';
  if (pathname === START_PATH) {
    const entries = (user.products ?? []).map((code) => productEntry(code, user.permissions));
    const [only] = entries;
    if (entries.length === 1 && only?.selfService && !canOpenAdmin(user)) {
      return only.path;
    }
    return START_PATH;
  }
  if (LANDING_ALIASES.has(pathname)) {
    return defaultHomePath(user);
  }
  return target;
}
